<template>
  <v-container>
    <v-row class="d-flex justify-center align-center"
      ><span>Display report for</span>
      <v-text-field
        class="mt-6 ml-2"
        dense
        :label="'NORAD ID'"
        :value="currentNoradId"
        @input="updateNoradID"
        :outlined="true"
        :dense="true"
        :rules="noradIDRules"
    /></v-row>
    <v-container v-if="currentNoradId != null && currentNoradId != ''">
      <template v-if="currentSatelliteEvents != null">
        <v-row>
          <v-col>
            <v-row><span class="text-h4 ml-n2 mb-4">Summary</span></v-row>
            <v-row><span class="text-h6">Event count by criticity</span></v-row>
            <v-row>
              <v-col
                v-for="{ criticity, count } in countByCriticitySorted"
                cols="3"
                :key="criticity.name + criticity.color"
                dense
              >
                <span v-bind:style="{ color: criticity.color }">
                  <b>{{ criticity.name }}:</b>
                </span>
                {{ count }}
              </v-col>
              <v-col v-if="countByCriticitySorted.length === 0">
                <span> No event detected for this satellite. </span>
              </v-col>
            </v-row>
            <v-row><span class="text-h6">Last maneuver</span></v-row>
            <v-row v-if="isInAlertOfType(currentNoradId, 'MANEUVER')">
              <v-col v-if="lastManeuverEvent != null"
                >Last maneuver detected on
                <strong>{{
                  getDisplayValueFromPath(
                    lastManeuverEvent.detectionDate,
                    ['detectionDate'],
                    'AbstractEvent'
                  ) || 'N/A'
                }}</strong></v-col
              >
              <v-col v-else>No maneuver detected</v-col>
            </v-row>
            <v-row v-else>
              <v-col
                ><span
                  >This satellite was not screened for maneuvers</span
                ></v-col
              ></v-row
            >
            <v-row
              ><span class="text-h6">Next close approach event</span></v-row
            >
            <v-row v-if="isInAlertOfType(currentNoradId, 'CLOSE_APPROACH')">
              <v-col v-if="nextCloseApproachEvent != null"
                >Next close approach by
                <strong>{{
                  getDisplayValueFromPath(
                    nextCloseApproachEvent.chaserNoradId,
                    ['chaserNoradId'],
                    'AbstractEvent'
                  ) || 'N/A'
                }}</strong>
                on
                <strong>{{
                  getDisplayValueFromPath(
                    nextCloseApproachEvent.detectionDate,
                    ['detectionDate'],
                    'AbstractEvent'
                  ) || 'N/A'
                }}</strong>
              </v-col>
              <v-col v-else>No future close approach detected</v-col>
            </v-row>
            <v-row v-else>
              <v-col
                ><span
                  >This satellite was not screened for close approaches</span
                ></v-col
              ></v-row
            >
            <v-row
              ><span class="text-h6"
                >Last detection of an orbit insertion</span
              ></v-row
            >
            <v-row v-if="isInAlertOfType(currentNoradId, 'ORBIT_INSERTION')">
              <v-col v-if="lastOrbitInsertionEvent != null"
                >Last detection of an orbit insertion on
                <strong>{{
                  getDisplayValueFromPath(
                    lastOrbitInsertionEvent.detectionDate,
                    ['detectionDate'],
                    'AbstractEvent'
                  ) || 'N/A'
                }}</strong></v-col
              >
              <v-col v-else>No orbit insertion detected</v-col>
            </v-row>

            <v-row v-else>
              <v-col
                ><span
                  >This satellite was not screened for orbit insertion
                  detection</span
                ></v-col
              ></v-row
            >
          </v-col>
          <v-col>
            <v-row><span class="text-h6">Detected events</span></v-row>
            <v-row>
              <ScreeningCatalogueAnalysisEventsTable
                style="width: 100%; height: 100%"
                :events="currentSatelliteEvents"
                :idMission="currentMission.id"
              />
            </v-row>
          </v-col>
        </v-row>
      </template>
      <template v-else
        ><v-row
          ><v-col
            ><span>No alerts are associated with this satellite</span></v-col
          ></v-row
        ></template
      >
    </v-container>
  </v-container>
</template>

<script>
import errorMixin from '../../mixins/logHandler.js'
import TooltipText from './TooltipText'
import ScreeningCatalogueAnalysisEventsTable from './ScreeningCatalogueAnalysisEventsTable'
import defenseMixin from '../../mixins/defense.js'
import { mapGetters } from 'vuex'

export default {
  name: 'ScreeningSingleSatelliteReport',
  mixins: [errorMixin, defenseMixin],
  components: { TooltipText, ScreeningCatalogueAnalysisEventsTable },
  data() {
    return {
      currentNoradId: null,
      noradIDRules: [
        (value) => {
          const pattern = /^(\d{5})?$/
          return value == null || pattern.test(value) || 'Invalid NORAD ID.'
        }
      ]
    }
  },

  watch: {},

  mounted() {},

  props: {
    events: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    ...mapGetters({
      currentResultsLog: 'currentResultsLog',
      currentMission: 'currentMission'
    }),

    currentSatelliteEvents() {
      return this.events.filter(
        (event) => event.eventTargetNoradId === this.currentNoradId
      )
    },
    lastManeuverEvent() {
      let lastManeuverEvent = null
      for (const event of this.currentSatelliteEvents) {
        if (
          event.eventType === 'MANEUVER' &&
          (lastManeuverEvent == null ||
            event.detectionDate > lastManeuverEvent.detectionDate)
        ) {
          lastManeuverEvent = event
        }
      }
      return lastManeuverEvent
    },
    nextCloseApproachEvent() {
      let nextCloseApproachEvent = null
      for (const event of this.currentSatelliteEvents) {
        if (
          event.eventType === 'CLOSE_APPROACH' &&
          (nextCloseApproachEvent == null ||
            event.detectionDate < nextCloseApproachEvent.detectionDate)
        ) {
          nextCloseApproachEvent = event
        }
      }
      return nextCloseApproachEvent
    },
    lastOrbitInsertionEvent() {
      let lastOrbitInsertionEvent = null
      for (const event of this.currentSatelliteEvents) {
        if (
          event.eventType === 'ORBIT_INSERTION' &&
          (lastOrbitInsertionEvent == null ||
            event.detectionDate > lastOrbitInsertionEvent.detectionDate)
        ) {
          lastOrbitInsertionEvent = event
        }
      }
      return lastOrbitInsertionEvent
    },

    countByCriticitySorted() {
      return this.currentSatelliteEvents
        .map((event) => event.criticity)
        .reduce((countByCriticity, criticity) => {
          const existingCriticityIndex = countByCriticity.findIndex(
            (criticityCount) =>
              criticityCount.criticity.name + criticityCount.criticity.color ===
              criticity.name + criticity.color
          )
          if (existingCriticityIndex !== -1) {
            countByCriticity[existingCriticityIndex].count += 1
          } else {
            countByCriticity.push({ criticity, count: 1 })
          }
          return countByCriticity
        }, [])
        .sort((a, b) => {
          if (a.criticity.priority - b.criticity.priority === 0) {
            return a.criticity.name < b.criticity.name ? -1 : 1
          } else {
            return a.criticity.priority - b.criticity.priority
          }
        })
    }
  },

  methods: {
    updateNoradID(value) {
      let valid = this.noradIDRules
        .map((rule) =>
          typeof rule === 'function' ? rule(value) === true : rule === true
        )
        .reduce((a, b) => a && b)
      if (valid) {
        this.currentNoradId = value
      }
    },
    isInAlertOfType(id, type) {
      let alertDefinitions =
        this.currentResultsLog.results.analysisResult.alertDefinitions
      return alertDefinitions.some((alertDefinition) => {
        if (
          alertDefinition.primarySpaceObjectGroups !== null &&
          alertDefinition.primarySpaceObjectGroups.length == 0
        ) {
          return alertDefinition.type === type
        } else if (alertDefinition.primarySpaceObjectGroups !== null) {
          return (
            alertDefinition.type === type &&
            alertDefinition.primarySpaceObjectGroups.some((group) => {
              return group.spaceObjectIdentifiers.includes(id)
            })
          )
        }
      })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
